import { push } from "connected-react-router"
import { ExclusiveChoice } from "dash-styled"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { cloneInstance } from "../../../../../../../../Actions/API/AppInstance/AppInstanceActions"
import {
    clearInputsForId,
    dirtinessChanged
} from "../../../../../../../../Actions/Client/InputRender/InputRenderActions"
import { useBoolean } from "../../../../../../../../Hooks/useBoolean"
import { useGroundTruthList } from "../../../../../../../../Hooks/useGroundTruthList"
import { useAppInstance } from "../../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../../Hooks/useAppTemplate"
import {
    DataPointsToUpdate,
    MappedJobStatus
} from "../../../../../../../../Models/API/APIJobsPerStatusModel"
import {
    getValue,
    hasValue
} from "../../../../../../../../Models/Client/FetchState"
import { ContactUsMessageType } from "../../../../../../../../Models/External/FeedbackModel"
import { MainThunkDispatch } from "../../../../../../../../Reducers"
import { routePaths } from "../../../../../../../../Routes"
import { Confirm } from "../../../../../../../Common/Modal/Confirm"
import { ContactForm } from "../../../../../../../Common/Modal/ContactForm"
import { UpdateDataPointsModal } from "../../../Common/UpdateDataPointsModal"
import { useJobCount } from "../../../../../../../../Hooks/useJobCount"

type ModalViewProps = {
    orgUsername: string
    appID: string
    jobFormData: any
    isSubmitting: boolean
    isRequestingChange: boolean
    updateDataPointModalEnabled?: boolean
    saving: boolean
    showUpdateDataPoint: boolean
    showUpdateDataPointModal: () => void
    hideUpdateDataPointModal: () => void
    endRequestingChange: () => void
    startSaving: () => void
    endSaving: () => void
    stopLiveValidatingForm: () => void
    endSubmitting: () => void
    onSubmit: (extraParams?: any, callback?: any) => void
}
// TODO: split modals on separate components
export const ModalView = ({
    orgUsername,
    appID,
    jobFormData,
    isSubmitting,
    isRequestingChange,
    updateDataPointModalEnabled,
    saving,
    showUpdateDataPoint,
    showUpdateDataPointModal,
    hideUpdateDataPointModal,
    endRequestingChange,
    startSaving,
    endSaving,
    stopLiveValidatingForm,
    endSubmitting,
    onSubmit
}: ModalViewProps) => {
    const [changeOption, setChangeOption] = useState(0)
    const [showContact, startShowContact, endShowContact] = useBoolean(false)

    const dispatch = useDispatch<MainThunkDispatch>()

    const instanceFetch = useAppInstance(appID)

    const templateFetch = useAppTemplate(appID)

    const groundTruthListFetch = useGroundTruthList(appID, {
        filter: [],
        sort: { desc: true, sortBy: "created" }
    })

    const jobCount = useJobCount(appID)

    useEffect(() => {
        if (
            !showContact &&
            isSubmitting &&
            hasValue(instanceFetch) &&
            !isRequestingChange &&
            !showUpdateDataPoint
        ) {
            endSubmitting()
        }
    }, [
        showContact,
        isSubmitting,
        isRequestingChange,
        showUpdateDataPoint,
        instanceFetch,
        endSubmitting
    ])

    const gtID = () => getValue(groundTruthListFetch)?.items?.[0]?.id

    const cloneProject = (
        appUUID: string,
        params: any,
        canoticCrowd: boolean,
        successMessage: string,
        errorMesssage: string,
        projectName: string
    ) =>
        dispatch(
            cloneInstance(
                appUUID,
                params,
                canoticCrowd,
                false,
                successMessage,
                errorMesssage,
                projectName
            )
        )
            .then(project => {
                dispatch(clearInputsForId(appUUID))
                dispatch(dirtinessChanged(false))
                if (project !== undefined) {
                    dispatch(
                        push(routePaths.appDetail(orgUsername, project.uuid))
                    )
                }
            })
            .catch(() => {})

    const description = () => {
        return (
            <>
                After the changes you just made, are all of your{" "}
                {gtID !== undefined && (
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-500"
                        href={`${routePaths.appCustomizeBaselineDataDetail(
                            orgUsername,
                            appID,
                            `${gtID()}`
                        )}`}
                    >
                        ground truth answers{" "}
                    </a>
                )}
                still correct?{" "}
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-500"
                    href={`${routePaths.docs(
                        "how-to-change-your-task-design#task-design-and-ground-truth-data"
                    )}`}
                >
                    Here's how to answer this
                </a>
                <ExclusiveChoice
                    choices={[
                        "Yes, all ground truth output is still valid",
                        "No, some ground truth output will now be invalid",
                        "I'm not sure"
                    ]}
                    selectedIndex={changeOption}
                    interactive={true}
                    onClick={setChangeOption}
                    withPanel={false}
                    color="violet"
                    className="w-max-content my-8 mx-auto"
                    testId="select-change-method"
                />
            </>
        )
    }

    if (showContact) {
        if (!hasValue(instanceFetch) || !hasValue(templateFetch)) {
            return null
        }

        return (
            <ContactForm
                prefilledMessage="Please help me understand whether my changes to my job design will affect my ground truth"
                extraData={{
                    appID: appID,
                    type: ContactUsMessageType.ChangeParams,
                    newParams: jobFormData,
                    oldParams: instanceFetch.value.appParams?.params,
                    projectName: instanceFetch.value.name,
                    templateName: templateFetch.value.name
                }}
                onClose={endShowContact}
                title={`Help editing params for ${instanceFetch.value.name}`}
            />
        )
    }

    if (!isSubmitting || !hasValue(instanceFetch)) return null

    if (isRequestingChange) {
        const p = {
            params: {
                ...instanceFetch?.value?.appParams?.params,
                ...jobFormData
            }
        }

        // should be creating a new instance
        const handleClickContinue = () => {
            switch (changeOption) {
                case 0:
                    showUpdateDataPointModal()
                    endRequestingChange()
                    break
                case 1:
                    if (!hasValue(templateFetch) || !hasValue(instanceFetch))
                        return
                    startSaving()

                    cloneProject(
                        appID,
                        p,
                        instanceFetch.value.canoticCrowd,
                        "Project cloned",
                        "Could not clone project",
                        `${instanceFetch.value.name} copy`
                    ).then(() => {
                        endSaving()
                        endRequestingChange()
                        stopLiveValidatingForm()
                    })
                    break
                case 2:
                    endSubmitting()
                    startShowContact()
                    break
            }
        }

        const yesLabel =
            updateDataPointModalEnabled && changeOption === 0
                ? "Next"
                : changeOption === 0
                ? saving
                    ? "Saving..."
                    : "Save changes"
                : changeOption === 1
                ? saving
                    ? "Cloning..."
                    : "Clone project"
                : "Contact support"

        return (
            <Confirm
                onClose={endRequestingChange}
                title="Does your ground truth still match this design?"
                description={description()}
                isProcessing={saving}
                yesLabel={yesLabel}
                onClickYes={handleClickContinue}
                extraValue={
                    changeOption === 1 ? (
                        <p className="text-center my-3 text-rhino-800 text-sm">
                            (without any data points or ground truth)
                        </p>
                    ) : undefined
                }
            />
        )
    }

    if (showUpdateDataPoint) {
        const handleSave = (updateCheckboxes?: DataPointsToUpdate) => {
            const cancelAndRelabelJobsInState: string[] = []
            if (updateCheckboxes) {
                Object.keys(updateCheckboxes).forEach(item => {
                    if (updateCheckboxes[item as MappedJobStatus]) {
                        cancelAndRelabelJobsInState.push(item)
                    }
                })
            }
            onSubmit(
                {
                    cancelAndRelabelJobsInState
                },
                hideUpdateDataPointModal
            )
        }

        return (
            <UpdateDataPointsModal
                isSaving={saving}
                jobCount={jobCount}
                handleSave={handleSave}
                handleClose={hideUpdateDataPointModal}
            />
        )
    }

    return null
}
